import Notification from "@blocks/Notification"
import UnexpectedSalonTypeError from "@errors/UnexpectedSalonTypeError"
import Router from '@services/router'
import React from 'react'
import { Link } from 'react-router-dom'
import toaster from "toasted-notes"

/* CONSTANTS */
export const UNAUTHENTICATED = "Unauthenticated."
export const CSRF_MISMATCH = "CSRF token mismatch."

/* FUNCTIONS */
export type valueof<T> = T[keyof T]

export const confirmation = (text: string): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        resolve(window.confirm(text))
    })
}

export const notify = (message: string, heading?: string, type?: "error" | "info", duration?: number) => {
    toaster.notify(({ onClose }) => <Notification onClose={onClose} heading={heading} message={message} type={type} />, {
        position: "top-right", duration: duration || 15000
    })
}

export const notifyError = (message: string, heading?: string, duration?: number) => {
    notify(message, heading, "error", duration)
}

export const notifyErrors = (errors: any) => {
    Object.entries(errors).map(
        ([ key, errors ]: any[]) => 
            errors.forEach((error: string) => notifyError(error))
    )
}

export const route = (name: string, ...params: (string | number | null)[]): string => {
    return Router.getHrefByName(name, ...params)
}

export const path = (name: string): string => {
    return Router.getPathByName(name)
}

export const getHeadingBySalon = (salon: Customer.Resources.Salon) => {
    const category = (salon.status === "unavailable") ? getCategoryByType(salon.type) : salon.status
    return <><Link to={route("customer:" + category)}>{getCategoryDescription(category)}</Link> — {salon.name}</>
}

export const getCategoryByType = (type: Attributes.Salon.Type) => {
    switch (type) {
        case 'current': return 'current'
        case 'finished': return 'past'
        case 'voting': return 'current'
        case 'future': return 'current'
        default: throw new UnexpectedSalonTypeError(type)
    }
}

export const getCategoryDescription = (category: "participating" | "current" | "past") => {
    switch (category) {
        case 'current': return 'Open Salons'
        case 'past': return 'Results'
        case 'participating': return 'Entries'
        default: throw "Unknown salon's category."
    }
}

export const toFileString = (string: string) => {
    return string.split(' ').join('_').toLowerCase()
}

export const capitalize = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export const debounce = <T extends (...args: any) => any>(func: T, wait: number): (...args: Parameters<T>[]) => void => {
    let timeout: number;

    return function executedFunction(...args: any[]) {
        const later = () => {
            clearTimeout(timeout)
            func(...args)
        }

        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }
}

export const asyncDebounce = <T extends (...args: any) => Promise<any>>(func: T, wait: number): (...args: Parameters<T>) => ReturnType<T> => {
    let timeout: number;

    return (...args: Parameters<T>) => {
        return new Promise((resolve, reject) => {
            const later = async () => {
                clearTimeout(timeout)
                const result = await func(...args) as ReturnType<T>
                resolve(result)
            }
    
            clearTimeout(timeout)
            timeout = setTimeout(later, wait)
        }) as ReturnType<T>
    } 
}

export const FormDataFromObject = (obj: object, method?: string) => {
    const formData = new FormData()
    Object.keys(obj).forEach((key: keyof typeof obj) => formData.append(key, obj[key]))
    if (method) formData.append("_method", method)
    return formData
}

export const formatBytes = (bytes?: number, decimals: number = 2) => {
    if (bytes === 0 || !bytes) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export const percent = (num1: number, num2: number): string => {
    let number = Math.round((num1 / num2) * 100) || 0
    return number + "%"
}

// CHECKS
export const loaded = (...data: any[]): boolean => {
    return data.filter(item => item !== undefined).length === data.length
}

export const isOdd = (number: number): boolean => {
    return number % 2 === 1
}

export const isEven = (number: number): boolean => {
    return !isOdd(number)
}

// STRINGS
export const pluralize = (count: number, singular: string): string => {
    const ending: string = (count === 1) ? "" : "s"
    return `${count} ${singular}${ending}`
} 

// DATES
export function formatDate(date?: undefined): null
export function formatDate(date: string | number): string
export function formatDate(date?: string | number): string | null {
    if (!date) return null
    return new Date(date).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

export const formatDateToMonthAndDay = (date?: string | number): string | null => {
    if (!date) return null
    return new Date(date).toLocaleDateString("en-US", { month: 'short', day: '2-digit', timeZone: 'UTC' })
}

// MODELS
export const formatJuryName = (jury: General.Resources.Jury) => {
    return `${jury.first_name} ${jury.last_name}` + (jury.title ? `, ${jury.title}` : '')
}

export function openInNewTab(url: string) {
    const page = window.open(url, "_blank")
    page?.focus()
}

export function unique<T = any>(value: T, index: number, self: T[]) {
    return self.indexOf(value) === index
}

export function uniqueByKey<T = object>(key: keyof T) {
    return (value: T, index: number, self: T[]) => self.findIndex(item => item[key] === value[key]) === index
}